<template>
  <div class="container">
    <BLoading :active.sync="isLoading" />
    <div v-if="!isLoading">
      <div class="columns quote-details-view--column-reverse">
        <div class="column quote-details-view--header">
          <h1>You Requested a {{ serviceName }}</h1>
          <h4 v-if="scheduleStatus !== 'new'">On {{ schedule.manualquote.created_at | date }}</h4>
          <LsTypicalPricing
            v-if="showPriceRange && serviceID && scheduleStatus === 'new'"
            test-id="typical_pricing"
            :property-id="property_id"
            :service-id="serviceID"
          />
        </div>
        <div class="column is-narrow quote-details-view--address">
          <LsAddressCard :property="selectedProperty" />
        </div>
      </div>
      <div
        v-if="scheduleStatus === 'new' || scheduleStatus === 'pending'"
        class="quote-details-view--schedule-card"
      >
        <LsIcon
          type="calendar"
          test-id="icon"
          size="md"
          class="calendar quote-details-view--calendar-icon"
        />
        <span v-if="schedule.contractor">
          {{ schedule.contractor.user.first_name }} {{ schedule.contractor.user.last_name }} is
          scheduled to visit your property
        </span>
        <span v-else>We are finding a crew to visit your property</span> between
        <strong>{{ schedule.next_starts_at | date }}</strong> and
        <strong>{{ schedule.next_ends_at | date }}</strong> to provide a quote
      </div>
      <p class="quote-details-view--schedule-description-container">
        <span class="quote-details-view--schedule-description-text">{{
          schedule.manualquote.description
        }}</span>
      </p>
      <div
        v-if="schedule.manualquote.images.length"
        class="quote-details-view--manual-quote-images"
      >
        <LsImage
          v-for="(image, idx) in schedule.manualquote.images"
          :key="idx"
          :src="image.image_url"
          class="quote-details--view--images"
        />
      </div>
      <div v-else-if="schedule.manualquote.price">
        <h4>
          <strong>{{ schedule.contractor.user.first_name }} sent you a quote</strong> on
          {{ schedule.manualquote.price.created_at | date }}
        </h4>
        <h4 class="quote-details-view--price-to-complete">
          <LsIcon test-id="icon" size="lg" type="quote" class="quote-details-view--quote-icon" />
          <strong class="quote-details-view--contractor-price-amount">{{ price }}</strong> to
          complete {{ schedule.manualquote.service.name }}
        </h4>
        <LsTypicalPricing
          v-if="showPriceRange && serviceID"
          test-id="typical_pricing_two"
          :property-id="property_id"
          :service-id="serviceID"
          class="quote-details-view--pricing"
          priced
        />
        <div class="quote-details-view--work-details">
          <div class="quote-details-view--completion-quote">
            <LsIcon type="calendar" class="quote-details-view--calendar-icon" />
            <span v-if="schedule.manualquote.status === 'priced'"
              >Pending your approval, {{ schedule.manualquote.service.name }} is</span
            >
            <span v-if="schedule.manualquote.status === 'authorized'">This work is</span>
            <span v-if="schedule.manualquote.status === 'rejected'">This work was</span>
            <span>
              scheduled to be completed on
              <strong>{{ schedule.manualquote.price.schedule_at | date }}</strong>
            </span>
          </div>
          <div class="quote-details-view--contractor-description">
            <i>"{{ schedule.manualquote.price.description }}"</i>
          </div>
          <div v-if="scheduleStatus === 'priced'" class="quote-details-view--valid-until">
            <p>
              <strong>
                This quote is valid until {{ schedule.manualquote.expires_at | date }}
              </strong>
            </p>
            <p>As always, your credit card will not be charged until after service is complete</p>
          </div>
        </div>
      </div>
      <div v-if="scheduleStatus === 'authorized'" class="quote-details-view--quote-status">
        <LsIcon type="checkmark" class="quote-details-view--icon" />
        You authorized this work on {{ schedule.manualquote.price.status_set_at | date }}
      </div>
      <div v-if="scheduleStatus === 'rejected'" class="quote-details-view--quote-status">
        <LsIcon type="cancel" class="quote-details-view--icon" color="danger" />
        You turned down this quote on {{ schedule.manualquote.price.status_set_at | date }}
      </div>
      <div v-if="scheduleStatus === 'cancelled'" class="quote-details-view--quote-status">
        <LsIcon type="cancel" class="quote-details-view--icon" color="danger" />
        This quote was cancelled on {{ schedule.manualquote.status_set_at | date }}
      </div>
      <div v-if="scheduleStatus === 'priced'" class="quote-details-view--footer">
        <LsButton
          text="turn down"
          bg-color="primary-light"
          text-color="primary"
          track-id="quote_details_view-turn_down"
          class="quote-details-view--button"
          :disabled="isApproving"
          @click="turnDown"
        />
        <LsButton
          :text="`Accept quote for ${price}`"
          track-id="quote_details_view-approve"
          class="quote-details-view--button"
          :loading="isApproving"
          @click="approveQuote"
        />
      </div>
      <LsButton
        v-if="scheduleStatus === 'new'"
        text="ok"
        track-id="quote_details_view-ok"
        @click="goBack"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import _ from 'lodash';
import { lsModalService, lsFilters, lsRouterHelpers, lsScheduleHelpers } from 'ls-vue-common';
import {
  SCHEDULES__APPROVE_QUOTE,
  PROPERTIES__SELECTED_PROPERTY,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  CONVERSATION__START_CONVERSATION,
  FEATURE_FLAGS__REGISTER_EXPOSURE,
} from '@/store/types';
import { LsAddressCard, LsButton, LsIcon, LsImage } from '@/components/common';
import { LsTypicalPricing } from '@/components';
import { TurnDownQuoteModal } from '@/modals';

export default {
  name: 'QuoteDetailsView',
  components: {
    LsAddressCard,
    LsTypicalPricing,
    LsButton,
    LsIcon,
    LsImage,
  },
  data() {
    return {
      property_id: lsRouterHelpers.getParam(this.$route, 'property_id'),
      schedule_id: lsRouterHelpers.getParam(this.$route, 'schedule_id'),
      isApproving: false,
      isCheckingRouting: true,
    };
  },
  computed: {
    ...mapState(['schedules', 'properties', 'conversation', 'featureFlags']),
    ...mapGetters([PROPERTIES__SELECTED_PROPERTY]),
    isLoading() {
      return (
        this.properties.loadingStatus.isLoading ||
        _.isNil(this.selectedProperty) ||
        _.isNil(this.schedule) ||
        this.isCheckingRouting
      );
    },
    showPriceRange() {
      return _.get(this.featureFlags.flags, ['customer.manualquotes.show_pricerange']);
    },
    price() {
      const { price } = this.schedule.manualquote;
      return lsFilters.currency({
        amount:
          _.get(price, 'amount_backup') && _.get(price, 'accepted') === 'backup'
            ? this.schedule.manualquote.price.amount_backup
            : this.schedule.manualquote.price.amount,
      });
    },
    selectedProperty() {
      return this[PROPERTIES__SELECTED_PROPERTY](this.property_id);
    },
    schedule() {
      return this.schedules.schedulesById[this.schedule_id];
    },
    serviceName() {
      return lsScheduleHelpers.getScheduleServiceName({ schedule: this.schedule });
    },
    scheduleStatus() {
      return lsScheduleHelpers.getScheduleStatus({
        schedule: this.schedule,
      });
    },
    serviceID() {
      return _.get(this.schedule, 'manualquote.service.service_id');
    },
  },
  async created() {
    this[FEATURE_FLAGS__REGISTER_EXPOSURE]({ flag: 'customer.manualquotes.show_pricerange' });

    await this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES]();

    if (_.isNil(this.selectedProperty) || _.isNil(this.schedule)) {
      return this.$router.push({
        name: 'services',
      });
    }
    if (this.schedule.type === 'instant_quote') {
      return this.$router.push({
        name: 'instantQuoteDetails',
      });
    }
    if (this.schedule.type !== 'manualquote') {
      return this.$router.push({
        name: 'serviceHistory',
      });
    }
    this.isCheckingRouting = false;
  },
  methods: {
    ...mapActions([
      SCHEDULES__APPROVE_QUOTE,
      PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
      CONVERSATION__START_CONVERSATION,
      FEATURE_FLAGS__REGISTER_EXPOSURE,
    ]),
    async approveQuote() {
      this.isApproving = true;
      await this[SCHEDULES__APPROVE_QUOTE]({ schedule_id: this.schedule_id });
      this.isApproving = false;
    },
    turnDown() {
      lsModalService.open({
        parent: this,
        component: TurnDownQuoteModal,
        props: {
          name: this.schedule.contractor.user.first_name,
          contractorUserId: this.schedule.contractor.user.user_id,
          property_id: _.toNumber(this.property_id),
          schedule_id: _.toNumber(this.schedule_id),
        },
      });
    },
    goBack() {
      this.$router.push({
        name: 'services',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@media (max-width: $mobile-max-width) {
  .quote-details-view--header {
    margin-top: -10px;
  }
  .quote-details-view--column-reverse {
    display: flex;
    flex-direction: column-reverse;
    height: 160px;
  }
  .quote-details-view--address {
    border-bottom: 1px solid $primary-light;
    margin-bottom: 15px;
    padding-bottom: 15px;
  }
}
h1 {
  font-weight: bold;
}
h2 > strong,
h4 > strong {
  color: $primary;
}
.quote-details-view--schedule-description-container {
  margin-bottom: 30px;
  padding-bottom: 30px;
  border-bottom: 1px solid $primary-light;
}
.quote-details-view--schedule-description-text {
  white-space: pre-wrap;
}
.quote-details-view--contractor-description {
  word-break: break-word;
  white-space: pre-wrap;
}
.quote-details-view--calendar-icon {
  vertical-align: sub;
  margin-right: 10px;
}
.quote-details-view--icon {
  vertical-align: sub;
  margin-right: 10px;
}
.quote-details-view--schedule-card {
  background-color: $primary-light;
  padding: 20px;
  margin-bottom: 20px;
  font-size: 14px;
}
.quote-details-view--manual-quote-images {
  margin-bottom: 20px;
  padding-bottom: 30px;
  border-bottom: 1px solid $primary-light;
}
.quote-details--view--images {
  display: inline-block;
  margin: 3px;
}
.quote-details-view--work-details {
  background-color: $primary-light;
  padding: 20px;
  margin-bottom: 20px;
  font-size: 14px;
}
.quote-details-view--quote-icon {
  margin-right: 10px;
  vertical-align: text-bottom;
}
.quote-details-view--pricing {
  margin-bottom: 20px;
}
.quote-details-view--price-to-complete {
  margin-bottom: 10px;
}
.quote-details-view--contractor-price-amount {
  font-size: 24px;
  margin-right: 5px;
}
.quote-details-view--completion-quote {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid $special-recommended-services;
}
.quote-details-view--valid-until {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid $special-recommended-services;
}
.quote-details-view--quote-status {
  font-size: 14px;
}
.quote-details-view--footer {
  display: flex;
  flex-wrap: wrap;
}
.quote-details-view--button {
  margin-right: 10px;
  @media (max-width: $mobile-max-width) {
    margin-bottom: 10px;
    margin-right: 0px;
  }
}
.themed-app {
  @media (max-width: $mobile-max-width) {
    .quote-details-view--address {
      border-color: $theme-color-primary-light;
    }
  }
  h2 > strong,
  h4 > strong {
    color: $theme-color-primary;
  }
  .quote-details-view--schedule-description-container {
    border-color: $theme-color-primary-light;
  }
  .quote-details-view--schedule-card {
    background-color: $theme-color-primary-light;
  }
  .quote-details-view--manual-quote-images {
    border-color: $theme-color-primary-light;
  }
  .quote-details-view--work-details {
    background-color: $theme-color-primary-light;
  }
  .quote-details-view--completion-quote {
    border-color: $theme-color-primary-light-hover;
  }
  .quote-details-view--valid-until {
    border-color: $theme-color-primary-light-hover;
  }
}
</style>
